import React, { Fragment } from "react";

import { HelperText } from "@common/components/HelperText/HelperText";
import { LogUnionRowItem } from "@common/components/MasterTable/models/MasterTableModel";
import { ConsoleLogEventRes, ConsoleLogType, LogType, LogUnionType, SystemEvent } from "@common/models/eventTracking";
import { vars } from "@common/styles";
import { helpCenterArticles } from "@common/util/helpCenterArticles";
import { showIntercomArticle } from "@common/util/intercom";

import * as Icon from "../../icons/icons";

export const iconForLog = (log: LogUnionRowItem, isError: boolean) => {
  if (isError) return <Icon.NetworkLogErrorIcon />;
  switch (log.instance_type) {
    case LogType.USER_EVENT:
      if (log.class_name.startsWith("#SHAKE")) return <Icon.ShakeIcon />;
      return <Icon.UserEventIcon />;
    case LogType.NOTIFICATION:
      return <Icon.NotificationIcon />;
    case LogType.SYSTEM_EVENT:
      return getSystemEventIcon(log);
    case LogType.SCREEN_EVENT:
      return <Icon.ScreenChangeEventIcon />;
    case LogType.CONSOLE_LOG:
      return getConsoleLogIcon(log);
    case LogType.CUSTOM_LOG:
      return <Icon.CustomLogIcon />;
    case LogType.NETWORK_REQUEST:
      if (log.request_type === "fetch") return <Icon.NetworkLogFetchIcon />;
      if (log.request_type === "document") return <Icon.NetworkLogDocumentIcon />;
      if (log.request_type === "stylesheet") return <Icon.NetworkLogStylesheetIcon />;
      if (log.request_type === "script") return <Icon.NetworkLogScriptIcon />;
      if (log.request_type === "font") return <Icon.NetworkLogFontIcon />;
      if (log.request_type === "image") return <NetworkLogImageIcon url={log.url} />;
      if (log.request_type === "manifest") return <Icon.NetworkLogManifestIcon />;
      if (log.content_type === "preflight") return <Icon.NetworkLogPreflightIcon />;
      return <Icon.NetworkLogDefaultIcon />; // media, ws, wasm, other
    default:
      return <></>;
  }
};

const NetworkLogImageIcon = ({ url }: { url: string }) => {
  return (
    <img
      style={{ width: 16, height: 16 }}
      src={url}
      alt={"Log icon"}
    />
  );
};

const getSystemEventIcon = (log: SystemEvent) => {
  const pausedEndings = ["Paused", "onPause", "Unfocused", "didEnterBackground", "willResignActive"];
  if (pausedEndings.some((ending) => log.description.includes(ending))) {
    return <Icon.SystemEventPausedIcon />;
  }
  return <Icon.SystemEventIcon />;
};

const getConsoleLogIcon = (log: ConsoleLogEventRes) => {
  switch (log.type) {
    case ConsoleLogType.VERBOSE:
      return <Icon.ConsoleLogIconVerbose />;
    case ConsoleLogType.DEBUG:
      return <Icon.ConsoleLogIconDebug />;
    case ConsoleLogType.INFO:
      return <Icon.ConsoleLogIconInfo />;
    case ConsoleLogType.WARNING:
      return <Icon.ConsoleLogIconWarning />;
    case ConsoleLogType.ERROR:
      return <Icon.ConsoleLogIconError />;
    default:
      return <Icon.ConsoleLogIconVerbose />;
  }
};

export const messageForLog = (log: LogUnionType, isError: boolean) => {
  const textStyle = {
    marginLeft: 8,
    overflow: "hidden",
    flex: "1",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: isError ? vars.colors.red : vars.colors.grey30,
  };
  switch (log.instance_type) {
    case LogType.USER_EVENT:
      if (log.class_name.startsWith("#SHAKE")) return <p style={textStyle}>{`${log.description}`}</p>;
      const description = log.description.replace(/\bthe\b\s*$/i, "").trimEnd();
      return <p style={textStyle}>{`${description} ${log.property || log.class_name}`}</p>;
    case LogType.NOTIFICATION:
      return <p style={textStyle}>{`${log.title} / ${log.description}`}</p>;
    case LogType.SYSTEM_EVENT:
      return <p style={textStyle}>{`${log.description}`}</p>;
    case LogType.SCREEN_EVENT:
      return <p style={textStyle}>{`${log.description}`}</p>;
    case LogType.CONSOLE_LOG:
      return <p style={textStyle}>{log.message}</p>;
    case LogType.CUSTOM_LOG:
      return <p style={textStyle}>{log.message}</p>;
    case LogType.NETWORK_REQUEST:
      return <p style={textStyle}>{getUrlLastPathSegment(log.url)}</p>;
  }
};

const getUrlLastPathSegment = (url: string): string | null => {
  try {
    const parsedUrl = new URL(url, "https://dummy.com");
    const segments = parsedUrl.pathname.split("/").filter(Boolean);
    const lastSegment = segments.length ? segments[segments.length - 1] : null;
    const query = parsedUrl.search;
    return lastSegment ? `${lastSegment}${query}` : "/";
  } catch {
    return url;
  }
};

export const getActivityHistoryHelperText = () => {
  return (
    <div style={{ position: "fixed", right: "22rem", top: "1.95rem" }}>
      <HelperText>
        <a onClick={() => showIntercomArticle(helpCenterArticles.activityHistory)}>{"Customize"}</a>
        {" Activity history."}
      </HelperText>
    </div>
  );
};
